import { Box, Typography } from '@mui/material';
import { env } from '@repo/env';

type Asset = {
  path: string;
  trim: string;
  dimensions: {
    mobile: {
      height: number;
      width: number;
    };
    desktop: {
      height: number;
      width: number;
    };
  };
};

type AssetImage = {
  src: string;
  srcSet: string;
  sizes: string;
  dimensions: {
    mobile: {
      height: number;
      width: number;
    };
    desktop: {
      height: number;
      width: number;
    };
  };
};

const IMAGE = {
  dimensions: {
    mobile: {
      height: 159,
      width: 202,
    },
    desktop: {
      height: 75,
      width: 123,
    },
  },
  trim: '177;744;485;743',
  path: 'cdn/banking/account-creation-banner-images/complete-rate-monitor-logo.png',
};

// Shared Cloudflare options:
// - format=auto: serve AVIF/WebP where supported (smaller at equal quality)
// - fit=scale-down: never enlarge past the source (no wasted bytes today; crisp once source art is hi-res)
// - quality=85 / sharpen=1: keep icons crisp when downscaled
const BASE_OPTS = 'format=auto,fit=scale-down,quality=85,sharpen=1';

function cf(asset: Asset, extra: string) {
  // trim runs before resize, so the artwork fills the frame and centers cleanly
  return `${env.NEXT_PUBLIC_NW_URL}/cdn-cgi/image/trim=${asset.trim},${extra},${BASE_OPTS}/${asset.path}`;
}

/**
 * Widths correspond to the mobile display box (252px) at 1x/2x/3x so high-DPR
 * phones pull a properly sized image. The browser picks via the `sizes` attr.
 */
const SRCSET_WIDTHS = [252, 504, 756];

function buildBannerImage(asset: Asset): AssetImage {
  const { mobile, desktop } = asset.dimensions;
  return {
    src: cf(asset, 'width=252'),
    srcSet: SRCSET_WIDTHS.map(w => `${cf(asset, `width=${w}`)} ${w}w`).join(', '),
    // Match the rendered width per breakpoint (md = 1080px) so the browser picks
    // the right srcSet candidate. Derived from this asset's own dimensions.
    sizes: `(max-width: 1080px) ${mobile.width}px, ${desktop.width}px`,
    dimensions: asset.dimensions,
  };
}

export function PromotionalBannerImage() {
  const image = buildBannerImage(IMAGE);
  return (
    <Box>
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <Typography
          sx={{ color: 'black', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box
            component="img"
            src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/nerdwallet-black-n-icon.svg`}
            height={21}
            width={21}
            alt=""
          />
          nerdwallet
        </Typography>
      </Box>
      <Box
        component="img"
        src={image.src}
        srcSet={image.srcSet}
        sizes={image.sizes}
        alt=""
        sx={{
          display: {
            xs: 'none',
            md: 'block',
          },
          height: {
            xs: image.dimensions.mobile.height,
            md: image.dimensions.desktop.height,
          },
          maxWidth: '100%',
          objectFit: 'contain',
          width: {
            xs: image.dimensions.mobile.width,
            md: image.dimensions.desktop.width,
          },
        }}
      />
    </Box>
  );
}
